import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, X, ChefHat } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const recipeSchema = z.object({
  title: z.string().trim().min(3, "Title must be at least 3 characters").max(100, "Title must be less than 100 characters"),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(500, "Description must be less than 500 characters"),
  prepTime: z.string().trim().min(1, "Prep time is required").max(50, "Prep time must be less than 50 characters"),
  servings: z.number().min(1, "Must serve at least 1").max(50, "Must serve 50 or fewer"),
  category: z.enum(["hot-flashes", "mood", "sleep", "energy", "bone-health"]),
});

type RecipeFormData = z.infer<typeof recipeSchema>;

interface AddRecipeDialogProps {
  onRecipeAdded: () => void;
}

export const AddRecipeDialog = ({ onRecipeAdded }: AddRecipeDialogProps) => {
  const [open, setOpen] = useState(false);
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [currentSymptom, setCurrentSymptom] = useState("");
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [currentIngredient, setCurrentIngredient] = useState("");
  const [instructions, setInstructions] = useState<string[]>([]);
  const [currentInstruction, setCurrentInstruction] = useState("");
  const [nutrients, setNutrients] = useState<string[]>([]);
  const [currentNutrient, setCurrentNutrient] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayName, setDisplayName] = useState<string>("");
  const { toast } = useToast();

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<RecipeFormData>({
    resolver: zodResolver(recipeSchema),
    defaultValues: {
      servings: 2,
    }
  });

  const fetchDisplayName = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", user.id)
          .maybeSingle();
        
        setDisplayName(profile?.display_name || "Anonymous");
      }
    } catch (error) {
      console.error("Error fetching display name:", error);
      setDisplayName("Anonymous");
    }
  };

  const addSymptom = () => {
    const trimmed = currentSymptom.trim();
    if (trimmed && trimmed.length <= 50 && symptoms.length < 10) {
      setSymptoms([...symptoms, trimmed]);
      setCurrentSymptom("");
    }
  };

  const removeSymptom = (index: number) => {
    setSymptoms(symptoms.filter((_, i) => i !== index));
  };

  const addIngredient = () => {
    const trimmed = currentIngredient.trim();
    if (trimmed && trimmed.length <= 200 && ingredients.length < 30) {
      setIngredients([...ingredients, trimmed]);
      setCurrentIngredient("");
    }
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const addInstruction = () => {
    const trimmed = currentInstruction.trim();
    if (trimmed && trimmed.length <= 500 && instructions.length < 20) {
      setInstructions([...instructions, trimmed]);
      setCurrentInstruction("");
    }
  };

  const removeInstruction = (index: number) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const addNutrient = () => {
    const trimmed = currentNutrient.trim();
    if (trimmed && trimmed.length <= 100 && nutrients.length < 15) {
      setNutrients([...nutrients, trimmed]);
      setCurrentNutrient("");
    }
  };

  const removeNutrient = (index: number) => {
    setNutrients(nutrients.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: RecipeFormData) => {
    if (ingredients.length === 0) {
      toast({
        title: "Missing ingredients",
        description: "Please add at least one ingredient",
        variant: "destructive",
      });
      return;
    }

    if (instructions.length === 0) {
      toast({
        title: "Missing instructions",
        description: "Please add at least one instruction step",
        variant: "destructive",
      });
      return;
    }

    if (symptoms.length === 0) {
      toast({
        title: "Missing symptoms",
        description: "Please add at least one symptom this recipe helps with",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to add recipes",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Fetch user's display name
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", user.id)
        .maybeSingle();

      const authorName = profile?.display_name || "Anonymous";

      console.log("Attempting to insert recipe with user_id:", user.id);

      const { error } = await supabase.from("user_recipes").insert({
        user_id: user.id,
        author_name: authorName,
        title: data.title,
        description: data.description,
        prep_time: data.prepTime,
        servings: data.servings,
        category: data.category,
        symptoms: symptoms,
        ingredients: ingredients,
        instructions: instructions,
        nutrients: nutrients,
      });

      if (error) {
        console.error("Insert error:", error);
        throw error;
      }

      console.log("Recipe added successfully!");

      toast({
        title: "Recipe added!",
        description: "Your recipe has been successfully added",
      });

      // Reset form
      reset();
      setSymptoms([]);
      setIngredients([]);
      setInstructions([]);
      setNutrients([]);
      setOpen(false);
      onRecipeAdded();
    } catch (error: any) {
      console.error("Error adding recipe:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add recipe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (isOpen) {
        fetchDisplayName();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Add Your Recipe
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5" />
            Share Your Wellness Recipe
          </DialogTitle>
          <DialogDescription>
            Add your favorite menopause-friendly recipe to help others on their wellness journey
          </DialogDescription>
          {displayName && (
            <div className="pt-2 text-sm text-muted-foreground">
              Recipe by: <span className="font-semibold text-foreground">{displayName}</span>
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Recipe Title *</Label>
              <Input
                id="title"
                {...register("title")}
                placeholder="e.g., Cooling Mint Smoothie"
                maxLength={100}
              />
              {errors.title && (
                <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Brief description of your recipe and its benefits..."
                maxLength={500}
                rows={3}
              />
              {errors.description && (
                <p className="text-sm text-destructive mt-1">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prepTime">Prep Time *</Label>
                <Input
                  id="prepTime"
                  {...register("prepTime")}
                  placeholder="e.g., 15 min"
                  maxLength={50}
                />
                {errors.prepTime && (
                  <p className="text-sm text-destructive mt-1">{errors.prepTime.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="servings">Servings *</Label>
                <Input
                  id="servings"
                  type="number"
                  {...register("servings", { valueAsNumber: true })}
                  min={1}
                  max={50}
                />
                {errors.servings && (
                  <p className="text-sm text-destructive mt-1">{errors.servings.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select onValueChange={(value) => setValue("category", value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hot-flashes">Hot Flashes</SelectItem>
                  <SelectItem value="mood">Mood Support</SelectItem>
                  <SelectItem value="sleep">Sleep Aid</SelectItem>
                  <SelectItem value="energy">Energy Boost</SelectItem>
                  <SelectItem value="bone-health">Bone Health</SelectItem>
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-destructive mt-1">{errors.category.message}</p>
              )}
            </div>
          </div>

          {/* Symptoms */}
          <div>
            <Label>Symptoms This Helps With * (max 10)</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={currentSymptom}
                onChange={(e) => setCurrentSymptom(e.target.value)}
                placeholder="e.g., Hot Flashes"
                maxLength={50}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSymptom())}
              />
              <Button type="button" onClick={addSymptom} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {symptoms.map((symptom, index) => (
                <Badge key={index} variant="secondary" className="gap-1">
                  {symptom}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => removeSymptom(index)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <Label>Ingredients * (max 30)</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={currentIngredient}
                onChange={(e) => setCurrentIngredient(e.target.value)}
                placeholder="e.g., 2 cups spinach"
                maxLength={200}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addIngredient())}
              />
              <Button type="button" onClick={addIngredient} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-1 mt-2">
              {ingredients.map((ingredient, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <span className="flex-1">{ingredient}</span>
                  <X
                    className="h-4 w-4 cursor-pointer text-muted-foreground hover:text-destructive"
                    onClick={() => removeIngredient(index)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div>
            <Label>Instructions * (max 20 steps)</Label>
            <div className="flex gap-2 mt-2">
              <Textarea
                value={currentInstruction}
                onChange={(e) => setCurrentInstruction(e.target.value)}
                placeholder="Add an instruction step..."
                maxLength={500}
                rows={2}
              />
              <Button type="button" onClick={addInstruction} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              {instructions.map((instruction, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="flex-1">{instruction}</span>
                  <X
                    className="h-4 w-4 cursor-pointer text-muted-foreground hover:text-destructive flex-shrink-0 mt-1"
                    onClick={() => removeInstruction(index)}
                  />
                </li>
              ))}
            </ol>
          </div>

          {/* Nutrients */}
          <div>
            <Label>Key Nutrients (optional, max 15)</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={currentNutrient}
                onChange={(e) => setCurrentNutrient(e.target.value)}
                placeholder="e.g., Vitamin D, Calcium"
                maxLength={100}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addNutrient())}
              />
              <Button type="button" onClick={addNutrient} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {nutrients.map((nutrient, index) => (
                <Badge key={index} variant="outline" className="gap-1">
                  {nutrient}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => removeNutrient(index)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding Recipe..." : "Add Recipe"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
